import React, { useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { WalletContext } from "../context/WalletContext";
import Header from "../components/Header";

const API_URL =
  process.env.REACT_APP_API_URL || "https://bitspend-api.onrender.com";
const MUSD_ADDRESS = "0x118917a40FAF1CD7a13dB0Ef56C86De7973Ac503";
const CARD_MANAGER_ADDRESS = "0x5Fa9bE59bA8500D77c817eE66E4aE09510B29b50";

const MUSD_ABI = [
  "function balanceOf(address account) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
];

export default function TopUp() {
  const { selectedWallet, provider, signer } = useContext(WalletContext);
  const location = useLocation();
  const navigate = useNavigate();

  const { cardId, cardAddress } = location.state || {};

  const [cardBalance, setCardBalance] = useState("0");
  const [walletMusdBalance, setWalletMusdBalance] = useState("0");
  const [topupAmount, setTopupAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(true);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    if (!selectedWallet) {
      alert("Wallet not connected! Redirecting to landing page...");
      navigate("/");
    }

    if (!cardId) {
      alert("No card selected! Redirecting to dashboard...");
      navigate("/");
    }
  }, [selectedWallet, cardId, navigate]);

  useEffect(() => {
    if (selectedWallet && cardId) {
      fetchBalances();
    }
  }, [selectedWallet, cardId]);

  /**
   * Ensure correct network before transaction
   */
  const ensureCorrectNetwork = async () => {
    try {
      if (!provider) {
        throw new Error("No provider available");
      }

      // Check current network
      const network = await provider.getNetwork();
      const currentChainId = network.chainId.toString();

      console.log("Current network:", currentChainId);

      if (currentChainId !== "31611") {
        console.log("Wrong network, switching to Mezo...");

        // Request switch
        try {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: "0x7B7B" }],
          });

          // Wait for switch
          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Verify switch
          const newNetwork = await provider.getNetwork();
          if (newNetwork.chainId.toString() !== "31611") {
            throw new Error("Failed to switch to Mezo testnet");
          }

          console.log("✅ Switched to Mezo testnet");
        } catch (switchError) {
          if (switchError.code === 4902) {
            // Network not added, add it
            await window.ethereum.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: "0x7B7B",
                  chainName: "Mezo Testnet",
                  nativeCurrency: {
                    name: "MEZO",
                    symbol: "MEZO",
                    decimals: 18,
                  },
                  rpcUrls: ["https://rpc.test.mezo.org"],
                  blockExplorerUrls: ["https://explorer.test.mezo.org"],
                },
              ],
            });

            await new Promise((resolve) => setTimeout(resolve, 2000));
          } else {
            throw switchError;
          }
        }
      }

      return true;
    } catch (err) {
      console.error("Network switch failed:", err);
      throw new Error("Please switch to Mezo Testnet in your wallet");
    }
  };

  const fetchBalances = async () => {
    try {
      setLoadingBalances(true);

      const cardResponse = await fetch(
        `${API_URL}/api/cards/${cardId}/balance`,
      );
      if (cardResponse.ok) {
        const cardData = await cardResponse.json();
        setCardBalance(cardData.data.balance);
      }

      if (provider) {
        const musdContract = new ethers.Contract(
          MUSD_ADDRESS,
          MUSD_ABI,
          provider,
        );
        const balance = await musdContract.balanceOf(selectedWallet);
        setWalletMusdBalance(ethers.formatUnits(balance, 18));
      }
    } catch (err) {
      console.error("Failed to fetch balances:", err);
      setError("Failed to load balances");
    } finally {
      setLoadingBalances(false);
    }
  };

  const handleTopUp = async () => {
    setError("");
    setLoading(true);

    const sendWithRetry = async (contract, method, args, description) => {
      setStatusMessage(description);
      let attempts = 0;
      const maxAttempts = 2;

      while (attempts < maxAttempts) {
        try {
          const currentSigner =
            attempts === 0
              ? signer
              : await (async () => {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                const freshProvider = new ethers.BrowserProvider(provider);
                return await freshProvider.getSigner();
              })();

          const freshContract = new ethers.Contract(
            contract.target,
            contract.interface,
            currentSigner,
          );

          const tx = await freshContract[method](...args);
          setStatusMessage(`Transaction sent: ${tx.hash.substring(0, 10)}...`); // ✅ Fixed
          await tx.wait();
          setStatusMessage(`${description} ✓`); // ✅ Fixed
          return tx;
        } catch (error) {
          attempts++;
          if (error.code === "NETWORK_ERROR" && attempts < maxAttempts) {
            console.log(`Network error, retrying (attempt ${attempts + 1})...`); // ✅ Fixed
            continue;
          }
          if (error.code === 4001 || error.code === "ACTION_REJECTED") {
            throw new Error("Transaction rejected by user");
          }
          throw error;
        }
      }
    };

    try {
      // ✅ ADD THIS: Check network BEFORE starting any transactions
      setStatusMessage("Ensuring correct network...");
      await ensureCorrectNetwork();

      const amount = parseFloat(topupAmount);
      if (!amount || amount <= 0) {
        throw new Error("Please enter a valid amount");
      }
      if (amount < 1) {
        throw new Error("Minimum top-up is 1 MUSD");
      }
      if (amount > parseFloat(walletMusdBalance)) {
        throw new Error("Insufficient MUSD balance in wallet");
      }
      if (!signer) {
        throw new Error("Wallet not connected properly");
      }

      const amountWei = ethers.parseUnits(amount.toString(), 18);

      setStatusMessage("Checking MUSD allowance...");
      const musdContract = new ethers.Contract(MUSD_ADDRESS, MUSD_ABI, signer);
      const currentAllowance = await musdContract.allowance(
        selectedWallet,
        CARD_MANAGER_ADDRESS,
      );

      if (currentAllowance < amountWei) {
        await sendWithRetry(
          musdContract,
          "approve",
          [CARD_MANAGER_ADDRESS, amountWei],
          "Please approve MUSD spending in your wallet...",
        );
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      const CardManager_ABI = [
        "function topUp(string memory cardId, uint256 amount) external",
      ];
      const cardManagerContract = new ethers.Contract(
        CARD_MANAGER_ADDRESS,
        CardManager_ABI,
        signer,
      );

      const topUpTx = await sendWithRetry(
        cardManagerContract,
        "topUp",
        [cardId, amountWei],
        "Please approve top-up transaction in your wallet...",
      );

      setStatusMessage("Refreshing balances...");
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await fetchBalances();

      setTopupAmount("");
      setStatusMessage("");
      alert(
        `✅ Successfully topped up ${amount} MUSD!\n\nTransaction: ${topUpTx.hash}`,
      );
    } catch (err) {
      console.error("Top-up error:", err);
      if (err.code === 4001 || err.code === "ACTION_REJECTED") {
        setError("Transaction rejected by user");
      } else if (err.message.includes("insufficient funds")) {
        setError("Insufficient MEZO for gas fees");
      } else if (err.code === "NETWORK_ERROR") {
        setError("Network connection issue. Please try again.");
      } else {
        setError(err.message || "Top-up failed");
      }
    } finally {
      setLoading(false);
      setStatusMessage("");
    }
  };

  const truncateAddress = (addr) =>
    addr ? `${addr.slice(0, 6)}...${addr.slice(-6)}` : "";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white p-4 pb-12 flex flex-col">
      <Header />

      <h1 className="text-2xl font-bold text-center my-4 sm:my-6 text-blue-300 drop-shadow-md">
        💰 Top Up Your Card
      </h1>

      {error && (
        <div className="max-w-md w-full mx-auto mb-4 bg-red-500/20 border border-red-500 rounded-xl p-4">
          <p className="text-red-200 text-sm">⚠️ {error}</p>
        </div>
      )}

      {loading && statusMessage && (
        <div className="max-w-md w-full mx-auto mb-4 bg-blue-500/20 border border-blue-500 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
            <p className="text-blue-200 text-sm">{statusMessage}</p>
          </div>
        </div>
      )}

      <div className="mt-4 sm:mt-6 max-w-md w-full mx-auto bg-gray-800 rounded-2xl shadow-lg p-6 flex flex-col gap-4 break-words">
        <h2 className="text-lg font-semibold text-blue-300 mb-2">
          Card Details
        </h2>

        <div className="flex justify-between items-center">
          <span className="text-gray-400 text-sm">Card ID:</span>
          <span className="text-white font-semibold">{cardId}</span>
        </div>

        <div className="flex justify-between items-start">
          <span className="text-gray-400 text-sm">Card Address:</span>
          <span className="text-white font-semibold break-all font-mono text-xs">
            {truncateAddress(cardAddress)}
          </span>
        </div>

        <div className="border-t border-gray-700 pt-4 mt-2">
          <div className="flex justify-between items-center mb-3">
            <span className="text-gray-400 text-sm">Card Balance:</span>
            <span className="text-green-400 font-bold text-lg">
              {loadingBalances
                ? "..."
                : `${parseFloat(cardBalance).toFixed(2)} MUSD`}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-sm">Wallet Balance:</span>
            <span className="text-white font-semibold">
              {loadingBalances
                ? "..."
                : `${parseFloat(walletMusdBalance).toFixed(2)} MUSD`}
            </span>
          </div>
        </div>

        <button
          onClick={fetchBalances}
          disabled={loadingBalances}
          className="text-blue-400 hover:text-blue-300 text-sm font-semibold transition"
        >
          {loadingBalances ? "Refreshing..." : "🔄 Refresh Balances"}
        </button>
      </div>

      <div className="mt-4 sm:mt-6 max-w-md w-full mx-auto bg-gray-800 rounded-2xl shadow-lg p-6 flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-blue-300">Top-Up Amount</h2>

        <div>
          <label className="text-gray-400 text-sm">Amount (MUSD)</label>
          <input
            type="number"
            step="0.01"
            min="1"
            value={topupAmount}
            onChange={(e) => setTopupAmount(e.target.value)}
            placeholder="Enter amount (minimum 1 MUSD)"
            className="w-full mt-2 bg-gray-700 py-3 px-4 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          />
          <p className="text-gray-500 text-xs mt-1">Minimum top-up: 1 MUSD</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setTopupAmount("10")}
            disabled={loading}
            className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-sm font-semibold transition"
          >
            10 MUSD
          </button>
          <button
            onClick={() => setTopupAmount("50")}
            disabled={loading}
            className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-sm font-semibold transition"
          >
            50 MUSD
          </button>
          <button
            onClick={() => setTopupAmount("100")}
            disabled={loading}
            className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-sm font-semibold transition"
          >
            100 MUSD
          </button>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <p className="text-blue-200 text-sm">
            💡 <strong>How it works:</strong>
          </p>
          <ul className="text-blue-200 text-xs mt-2 space-y-1 ml-4 list-disc">
            <li>You'll approve MUSD spending (one-time)</li>
            <li>Then confirm the top-up transaction</li>
            <li>Your card balance updates instantly</li>
          </ul>
        </div>

        <button
          onClick={handleTopUp}
          disabled={loading || !topupAmount || parseFloat(topupAmount) <= 0}
          className={`w-full mt-2 py-3 rounded-xl text-lg font-bold shadow-lg transition ${loading || !topupAmount || parseFloat(topupAmount) <= 0
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
            }`}
        >
          {loading ? "Processing..." : "Top Up Card"}
        </button>

        <button
          onClick={() => navigate("/")}
          disabled={loading}
          className="w-full bg-gray-700 hover:bg-gray-600 py-2 rounded-xl text-sm font-semibold transition"
        >
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
}
